// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the one window a file opens in, wherever Konvo previews files: chat, mail and WhatsApp
// attachments, Ask, Media and files, and Agents (files and attachments). The file's icon, name and a
// quiet line (size, type, path), a caller's note and actions, Full screen and Download, and the
// file below: FileContentSurface for what file_preview draws, or a caller's own view (the Agents
// code view, the document viewer). A view can put its own switches in the middle of the header
// (FilePreviewToolbarContext); on phones that place is at the end of the actions, for a view's ⋯.
// On phones the header has the phone header look (PhoneHeader): a glass close pill on the left and
// the actions in one glass capsule. Change the window here and it changes everywhere.
// Full screen shows only the file, edge to edge (the screen's own full screen where the browser
// allows it, else the whole window), with one small glass pill: Exit full screen, Download, Close.
// On a pointer device the pill fades after a moment and comes back when the pointer moves or nears it.

import * as Modal from '@app/features/app/components/dialogs/Modal';
import {FilePreviewToolbarContext} from '@app/features/file_preview/FilePreviewToolbar';
import {FileTypeIcon} from '@app/features/file_preview/FileTypeIcon';
import {PhoneBackButton, phoneHeaderStyles} from '@app/features/ui/phone_header/PhoneHeader';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {IconArrowsMaximize, IconArrowsMinimize, IconDownload, IconX} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {type ReactNode, useCallback, useEffect, useRef, useState} from 'react';
import styles from './FilePreviewWindow.module.css';

const DOWNLOAD_DESCRIPTOR = msg({message: 'Download', comment: 'Attachment preview action.'});
const FULL_SCREEN_DESCRIPTOR = msg({message: 'Full screen', comment: 'Attachment preview action.'});
const EXIT_FULL_SCREEN_DESCRIPTOR = msg({message: 'Exit full screen', comment: 'Attachment preview action.'});
const CLOSE_DESCRIPTOR = msg({message: 'Close', comment: 'Attachment preview action.'});

/** How long the full screen pill stays after the pointer last moved, on pointer devices. */
const PILL_IDLE_MS = 2000;
/** An Escape this soon after the browser left its full screen was the one that left it. */
const ESCAPE_AFTER_EXIT_MS = 400;

/**
 * Full screen for the window's root: the browser's own (Fullscreen API) on desktop where it is allowed,
 * else the window simply fills the app. Escape leaves full screen before it would close the window.
 */
function useFullScreen(useScreen: boolean) {
	const rootRef = useRef<HTMLDivElement | null>(null);
	const [full, setFull] = useState(false);
	/** Whether the browser's own full screen is on for the root. */
	const onScreenRef = useRef(false);
	const exitedAtRef = useRef(0);

	const exit = useCallback(() => {
		setFull(false);
		if (onScreenRef.current && document.fullscreenElement === rootRef.current) {
			void document.exitFullscreen().catch(() => {});
		}
	}, []);

	const enter = useCallback(() => {
		setFull(true);
		const root = rootRef.current;
		if (!useScreen || !root || !document.fullscreenEnabled) return;
		root
			.requestFullscreen({navigationUI: 'hide'})
			.then(() => {
				onScreenRef.current = true;
			})
			.catch(() => {});
	}, [useScreen]);

	useEffect(() => {
		const onChange = () => {
			if (!onScreenRef.current || document.fullscreenElement === rootRef.current) return;
			// The browser left its full screen (Escape, its own controls): leave ours too.
			onScreenRef.current = false;
			exitedAtRef.current = performance.now();
			setFull(false);
		};
		document.addEventListener('fullscreenchange', onChange);
		return () => {
			document.removeEventListener('fullscreenchange', onChange);
			if (onScreenRef.current && document.fullscreenElement === rootRef.current) {
				void document.exitFullscreen().catch(() => {});
			}
		};
	}, []);

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key !== 'Escape') return;
			const justLeft = performance.now() - exitedAtRef.current < ESCAPE_AFTER_EXIT_MS;
			if (!full && !justLeft) return;
			event.preventDefault();
			event.stopImmediatePropagation();
			if (full) exit();
		};
		window.addEventListener('keydown', onKey, true);
		return () => window.removeEventListener('keydown', onKey, true);
	}, [full, exit]);

	return {rootRef, full, enter, exit};
}

/** True a moment after the pointer last moved in the element; touch screens never go idle. */
function usePointerIdle(rootRef: React.RefObject<HTMLDivElement | null>, active: boolean) {
	const [idle, setIdle] = useState(false);
	useEffect(() => {
		setIdle(false);
		const root = rootRef.current;
		if (!active || !root || !window.matchMedia('(hover: hover)').matches) return;
		let timer = window.setTimeout(() => setIdle(true), PILL_IDLE_MS);
		const wake = () => {
			setIdle(false);
			window.clearTimeout(timer);
			timer = window.setTimeout(() => setIdle(true), PILL_IDLE_MS);
		};
		root.addEventListener('pointermove', wake);
		root.addEventListener('pointerdown', wake);
		return () => {
			window.clearTimeout(timer);
			root.removeEventListener('pointermove', wake);
			root.removeEventListener('pointerdown', wake);
		};
	}, [rootRef, active]);
	return idle;
}

/** A round icon button in the window's header, with its label as tooltip and accessible name. */
export function FilePreviewIconButton({
	label,
	icon,
	onClick,
	disabled,
	'data-flx': dataFlx,
}: {
	label: string;
	icon: ReactNode;
	onClick: () => void;
	disabled?: boolean;
	'data-flx'?: string;
}) {
	return (
		<button
			type="button"
			className={styles.iconButton}
			onClick={onClick}
			disabled={disabled}
			aria-label={label}
			title={label}
			data-flx={dataFlx}
		>
			{icon}
		</button>
	);
}

export interface FilePreviewWindowProps {
	/** The file's name, the window's title. */
	name: string;
	/** The title's tooltip; the name by default. */
	nameTooltip?: string;
	/** Its type, for the icon when the name has no extension. */
	contentType?: string;
	/** Instead of the icon by type (Agents: its own icons for code). */
	icon?: ReactNode;
	/** Beside the title, e.g. how an agent's chat changed the file. */
	titleExtra?: ReactNode;
	/** The quiet line under the title: size, type, path. */
	subline?: ReactNode;
	/** After the quiet line, e.g. "Outside the project · Only you can open it". */
	note?: ReactNode;
	/** The caller's own header buttons (FilePreviewIconButton), before Full screen and Download. */
	actions?: ReactNode;
	/** Shows Download; null keeps it hidden. */
	onDownload?: (() => void) | null;
	downloadDisabled?: boolean;
	/** A row under the header (Agents on phones: the change chip and Show in Files). */
	belowHeader?: ReactNode;
	onClose: () => void;
	/** Extra class on the window, e.g. for a caller's colour variables. */
	className?: string;
	/** The window's data-flx. */
	'data-flx'?: string;
	/** Its buttons' data-flx add .close, .download, .full-screen and .full-screen-pill(.exit/.download/.close) to this; the window's data-flx by default. */
	flxPrefix?: string;
	children: ReactNode;
}

export const FilePreviewWindow = observer(function FilePreviewWindow({
	name,
	nameTooltip,
	contentType,
	icon,
	titleExtra,
	subline,
	note,
	actions,
	onDownload,
	downloadDisabled,
	belowHeader,
	onClose,
	className,
	'data-flx': rootFlx = 'file-preview.window',
	flxPrefix,
	children,
}: FilePreviewWindowProps) {
	const flx = flxPrefix ?? rootFlx;
	const {i18n} = useLingui();
	const phone = MobileLayout.isMobileLayout();
	const {rootRef, full, enter, exit} = useFullScreen(!phone);
	const idle = usePointerIdle(rootRef, full);
	const [toolbar, setToolbar] = useState<HTMLDivElement | null>(null);
	return (
		<Modal.Root
			ref={rootRef}
			size="xlarge"
			centered={!phone}
			className={clsx(styles.window, full && styles.full, className)}
			onClose={onClose}
			data-flx={rootFlx}
			data-full-screen={full || undefined}
		>
			<Modal.ScreenReaderLabel text={name} />
			<div className={styles.windowHeader} hidden={full}>
				{phone && (
					<PhoneBackButton kind="close" label={i18n._(CLOSE_DESCRIPTOR)} onClick={onClose} data-flx={`${flx}.close`} />
				)}
				<span className={styles.windowIcon}>
					{icon ?? <FileTypeIcon fileName={name} contentType={contentType} size={20} />}
				</span>
				<div className={styles.windowTitles}>
					<span className={styles.windowTitleRow}>
						<span className={styles.windowTitle} title={nameTooltip ?? name}>
							{name}
						</span>
						{titleExtra}
					</span>
					{(subline || note) && (
						<span className={styles.windowSub}>
							{subline}
							{note}
						</span>
					)}
				</div>
				{!phone && <div ref={setToolbar} className={styles.windowToolbar} />}
				<div
					className={clsx(styles.windowActions, phone && phoneHeaderStyles.glass, phone && phoneHeaderStyles.capsule)}
				>
					{actions}
					<FilePreviewIconButton
						label={i18n._(FULL_SCREEN_DESCRIPTOR)}
						icon={<IconArrowsMaximize size={17} stroke={1.8} />}
						onClick={enter}
						data-flx={`${flx}.full-screen`}
					/>
					{onDownload && (
						<FilePreviewIconButton
							label={i18n._(DOWNLOAD_DESCRIPTOR)}
							icon={<IconDownload size={18} stroke={1.7} />}
							onClick={onDownload}
							disabled={downloadDisabled}
							data-flx={`${flx}.download`}
						/>
					)}
					{phone && <div ref={setToolbar} className={styles.windowToolbar} />}
					{!phone && <Modal.InsetCloseButton inline onClick={onClose} data-flx={`${flx}.close`} />}
				</div>
			</div>
			{!full && belowHeader}
			<div className={styles.windowBody}>
				<FilePreviewToolbarContext.Provider value={toolbar}>{children}</FilePreviewToolbarContext.Provider>
			</div>
			{full && (
				<div className={styles.pill} data-idle={idle || undefined} data-flx={`${flx}.full-screen-pill`}>
					<button
						type="button"
						className={clsx(styles.pillButton, styles.pillButtonLead)}
						onClick={exit}
						aria-label={i18n._(EXIT_FULL_SCREEN_DESCRIPTOR)}
						title={i18n._(EXIT_FULL_SCREEN_DESCRIPTOR)}
						data-flx={`${flx}.full-screen-pill.exit`}
					>
						<IconArrowsMinimize size={16} stroke={1.9} />
					</button>
					{onDownload && (
						<button
							type="button"
							className={styles.pillButton}
							onClick={onDownload}
							disabled={downloadDisabled}
							aria-label={i18n._(DOWNLOAD_DESCRIPTOR)}
							title={i18n._(DOWNLOAD_DESCRIPTOR)}
							data-flx={`${flx}.full-screen-pill.download`}
						>
							<IconDownload size={16} stroke={1.9} />
						</button>
					)}
					<span className={styles.pillDivider} />
					<button
						type="button"
						className={styles.pillButton}
						onClick={onClose}
						aria-label={i18n._(CLOSE_DESCRIPTOR)}
						title={i18n._(CLOSE_DESCRIPTOR)}
						data-flx={`${flx}.full-screen-pill.close`}
					>
						<IconX size={16} stroke={2} />
					</button>
				</div>
			)}
		</Modal.Root>
	);
});
